import { useEffect, useRef, useState } from "react";
import { PhoneCall, Upload, Search, UserPlus, Trash2, ChevronDown, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CALL_STATUSES = [
  { id: "pending", label: "Pendente", cls: "border-gray-500 text-gray-400 bg-gray-500/10" },
  { id: "no_answer", label: "Não atendeu", cls: "border-yellow-500 text-yellow-400 bg-yellow-500/10" },
  { id: "called", label: "Falou", cls: "border-blue-500 text-blue-400 bg-blue-500/10" },
  { id: "interested", label: "Interessado", cls: "border-success text-success bg-success/10" },
  { id: "do_not_call", label: "Não ligar", cls: "border-red-700 text-red-500 bg-red-700/10" },
];
const stCls = (s) => CALL_STATUSES.find((x) => x.id === s)?.cls || CALL_STATUSES[0].cls;
const stLabel = (s) => CALL_STATUSES.find((x) => x.id === s)?.label || "Pendente";

const telHref = (p) => `tel:${String(p || "").replace(/[^\d+]/g, "")}`;

export const CallListSection = ({ canClear = false }) => {
  const [data, setData] = useState({ customers: [], stats: { customers: 0, references: 0, pending: 0 } });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sps, setSps] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const load = async (qq = q, st = status, asg = assignee) => {
    try {
      const r = await api.get("/call-list", { params: { q: qq, status: st, assigned: asg } });
      setData(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => {
    load();
    api.get("/salespeople", { params: { scope: "group" } }).then((r) => setSps(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const id = setTimeout(() => load(q, status, assignee), 350);
    return () => clearTimeout(id);
  }, [q, status, assignee]); // eslint-disable-line

  const upload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let nNew = 0, nUpd = 0, nRefs = 0, ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data: r } = await api.post("/call-list/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
        });
        nNew += r.customers_new; nUpd += r.customers_updated; nRefs += r.references_new; ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "erro ao importar"}`);
      }
    }
    if (ok) toast.success(`${nNew} cliente(s) novo(s) · ${nRefs} referência(s) · ${nUpd} atualizado(s)`);
    setUploading(false);
    load();
  };

  const setCallStatus = async (cid, st) => {
    try {
      await api.patch(`/call-list/${cid}`, { call_status: st });
      load();
    } catch { toast.error("Erro ao salvar o status"); }
  };

  const appendNote = async (cid, text) => {
    try {
      await api.patch(`/call-list/${cid}`, { note_append: text });
      toast.success("Andamento registrado");
      load();
    } catch { toast.error("Erro ao salvar a anotação"); }
  };

  const assign = async (cid, spId) => {
    const sp = sps.find((s) => s.id === spId);
    try {
      await api.patch(`/call-list/${cid}`, { assigned_to_id: spId, assigned_to_name: sp?.name || "" });
      toast.success(spId ? `Ligação atribuída pra ${sp?.name}` : "Atribuição removida");
      load();
    } catch { toast.error("Erro ao atribuir"); }
  };

  const [importingLeads, setImportingLeads] = useState(false);

  const fromLeads = async () => {
    if (!window.confirm("Trazer todos os leads NÃO fechados pra lista de ligações?")) return;
    setImportingLeads(true);
    try {
      const { data: r } = await api.post("/call-list/from-leads");
      toast.success(`${r.added} lead(s) na lista de ligações · ${r.skipped} já estavam ou sem telefone`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao trazer os leads");
    } finally { setImportingLeads(false); }
  };

  const toLead = async (c) => {
    try {
      const { data: r } = await api.post(`/call-list/${c.id}/to-lead`);
      toast.success(r.reactivated ? `Lead de ${c.name} reativado no funil!` : `${c.name} virou lead!`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar o lead"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Apagar TODA a lista de ligações? Não dá pra desfazer.")) return;
    try {
      const { data: r } = await api.delete("/call-list");
      toast.success(`${r.deleted} contato(s) removido(s)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao limpar"); }
  };

  const NoteLog = ({ c }) => {
    const [txt, setTxt] = useState("");
    const send = () => { if (txt.trim()) { appendNote(c.id, txt.trim()); setTxt(""); } };
    return (
      <div className="mb-2">
        <div className="flex gap-2">
          <input
            data-testid={`call-notes-${c.id}`}
            value={txt}
            onChange={(e) => setTxt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="O que rolou na ligação..."
            className="bg-background border border-border focus:border-primary focus:outline-none px-3 h-9 text-xs flex-1"
          />
          <button
            data-testid={`call-notes-save-${c.id}`}
            onClick={send}
            disabled={!txt.trim()}
            className="bg-[#C9A227] disabled:opacity-40 text-black px-3 h-9 text-[9px] font-display font-black uppercase tracking-widest"
          >
            Salvar
          </button>
        </div>
        {c.notes && (
          <pre data-testid={`call-history-${c.id}`} className="whitespace-pre-wrap font-sans text-[11px] text-text-secondary mt-1.5 bg-background/60 border border-border/50 p-2 max-h-36 overflow-y-auto">{c.notes}</pre>
        )}
      </div>
    );
  };

  const ContactRow = ({ c, isRef = false }) => (
    <div className={`flex flex-wrap items-center gap-2 py-2.5 ${isRef ? "pl-4 border-l-2 border-[#C9A227]/40" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold text-sm truncate">
          {c.name}
          {isRef && c.relationship && <span className="text-[10px] text-text-secondary font-normal ml-1.5">({c.relationship})</span>}
        </p>
        {c.phone ? (
          <a href={telHref(c.phone)} onClick={(e) => e.stopPropagation()} data-testid={`call-phone-${c.id}`}
            className="text-xs text-[#C9A227] font-mono inline-flex items-center gap-1 hover:underline">
            <Phone size={10} /> {c.phone}
          </a>
        ) : <p className="text-xs text-text-secondary">sem telefone</p>}
      </div>
      <select
        data-testid={`call-status-${c.id}`}
        value={c.call_status || "pending"}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setCallStatus(c.id, e.target.value)}
        className={`border px-2 h-8 text-[10px] font-display font-bold uppercase tracking-wider bg-background ${stCls(c.call_status)}`}
      >
        {CALL_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      {c.lead_id ? (
        <span data-testid={`call-islead-${c.id}`} className="text-[9px] font-display font-black uppercase tracking-widest text-success inline-flex items-center gap-1 px-2">
          <Check size={11} /> Lead
        </span>
      ) : (
        <button
          data-testid={`call-tolead-${c.id}`}
          onClick={(e) => { e.stopPropagation(); toLead(c); }}
          className="border border-border hover:border-[#C9A227] hover:text-[#C9A227] h-8 px-2.5 text-[9px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1 transition-colors"
        >
          <UserPlus size={11} /> Virar lead
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="call-list-section">
      <div
        data-testid="call-list-dropzone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed p-5 mb-4 text-center cursor-pointer transition-colors ${dragging ? "border-[#C9A227] bg-[#C9A227]/10" : "border-border hover:border-[#C9A227]/60"}`}
      >
        <Upload size={20} className="mx-auto mb-1.5 text-[#C9A227]" />
        <p className="text-sm font-display font-bold uppercase tracking-wider">
          {uploading ? "Importando..." : "Arraste o export do DealerCenter aqui"}
        </p>
        <p className="text-[10px] text-text-secondary mt-1">
          ou clique pra escolher — Excel, CSV, Numbers ou PDF · puxo clientes, telefones, carro comprado e as referências
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          data-testid="call-list-from-leads"
          onClick={fromLeads}
          disabled={importingLeads}
          className="bg-[#C9A227] hover:opacity-90 disabled:opacity-50 text-black px-4 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5"
        >
          <PhoneCall size={12} /> {importingLeads ? "Trazendo..." : "Trazer leads não fechados"}
        </button>
        <input ref={fileRef} data-testid="call-list-file-input" type="file" multiple accept=".csv,.txt,.tsv,.xlsx,.xlsm,.numbers,.pdf" className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            data-testid="call-list-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar nome ou telefone..."
            className="bg-background border border-border focus:border-primary focus:outline-none pl-8 pr-3 h-10 text-sm w-full"
          />
        </div>
        <select data-testid="call-list-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-background border border-border px-2 h-10 text-xs">
          <option value="">Todos status</option>
          {CALL_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {canClear && data.stats.customers > 0 && (
          <button data-testid="call-list-clear" onClick={clearAll}
            className="border border-red-800/60 text-red-400 hover:bg-red-950/40 px-3 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5">
            <Trash2 size={12} /> Limpar
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="call-assignee-tabs">
        {[{ id: "", name: "Todos" }, ...sps, { id: "none", name: "Sem vendedor" }].map((s) => {
          const count = s.id === ""
            ? Object.values(data.stats.assignee_counts || {}).reduce((a, b) => a + b, 0)
            : (data.stats.assignee_counts || {})[s.id] || 0;
          return (
            <button
              key={s.id || "all"}
              data-testid={`call-tab-${s.id || "all"}`}
              onClick={() => setAssignee(s.id)}
              className={`px-3 h-9 text-[10px] font-display font-black uppercase tracking-widest border transition-colors ${assignee === s.id ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-border text-text-secondary hover:border-[#C9A227]/60"}`}
            >
              {s.name} ({count})
            </button>
          );
        })}
      </div>

      <p data-testid="call-list-stats" className="text-xs text-text-secondary mb-4">
        <PhoneCall size={12} className="inline mr-1 text-[#C9A227]" />
        {data.stats.customers} cliente(s) · {data.stats.references} referência(s) · {data.stats.pending} pendente(s) pra ligar
      </p>

      {data.customers.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center text-text-secondary text-sm">
          Nenhum contato ainda — baixa o export de clientes no DealerCenter (Reports → Customer List → Export) e arrasta o arquivo na área acima
        </div>
      )}

      <div className="space-y-2">
        {data.customers.map((c) => (
          <div key={c.id} data-testid={`call-customer-${c.id}`} className="border border-border bg-surface">
            <button type="button" onClick={() => setOpen(open === c.id ? null : c.id)}
              className="w-full flex items-center gap-3 p-3 text-left">
              <div className="min-w-0 flex-1">
                <p className="font-display font-black text-sm uppercase truncate">{c.name}</p>
                <p className="text-[11px] text-text-secondary truncate">
                  {c.phone || "sem telefone"}
                  {c.vehicle ? ` · ${c.vehicle}` : ""}
                  {c.sale_date ? ` · ${c.sale_date}` : ""}
                  {c.references.length > 0 ? ` · ${c.references.length} ref` : ""}
                </p>
              </div>
              <span className={`border px-2 py-0.5 text-[9px] font-display font-bold uppercase tracking-wider ${stCls(c.call_status)}`}>
                {stLabel(c.call_status)}
              </span>
              {c.assigned_to_name && (
                <span data-testid={`call-assigned-${c.id}`} className="border border-[#C9A227]/50 text-[#C9A227] px-2 py-0.5 text-[9px] font-display font-bold uppercase tracking-wider">
                  {c.assigned_to_name.split(" ")[0]}
                </span>
              )}
              <ChevronDown size={15} className={`text-text-secondary transition-transform ${open === c.id ? "rotate-180" : ""}`} />
            </button>
            {open === c.id && (
              <div className="px-3 pb-3 border-t border-border/50">
                <div className="flex items-center gap-2 mt-2 mb-1">
                  <label className="text-[9px] font-display font-black uppercase tracking-widest text-text-secondary">Quem vai ligar:</label>
                  <select
                    data-testid={`call-assign-${c.id}`}
                    value={c.assigned_to_id || ""}
                    onChange={(e) => assign(c.id, e.target.value)}
                    className="bg-background border border-border px-2 h-8 text-xs"
                  >
                    <option value="">Sem vendedor</option>
                    {sps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <ContactRow c={c} />
                <NoteLog c={c} />
                {c.references.length > 0 && (
                  <p className="text-[9px] font-display font-black uppercase tracking-widest text-text-secondary mt-1">Referências</p>
                )}
                {c.references.map((r) => (
                  <div key={r.id}>
                    <ContactRow c={r} isRef />
                    <div className="pl-4">
                      <NoteLog c={r} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CallListSection;
